import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { trackMetaEvent } from "@/lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("vivoz_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [tokens, setTokens] = useState(0);
    const [plan, setPlan] = useState("free");
    const [allowedScenarios, setAllowedScenarios] = useState(null);
    const [dailyUsage, setDailyUsage] = useState({ used: 0, limit: null });
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const t = localStorage.getItem("vivoz_token");
        if (!t) {
            setUser(null); setLoading(false);
            return;
        }
        try {
            const r = await api.get("/auth/me");
            setUser(r.data.user);
            setTokens(r.data.tokens_available);
            setPlan(r.data.plan);
            setAllowedScenarios(r.data.allowed_scenarios);
            setDailyUsage({ used: r.data.daily_sessions_used, limit: r.data.daily_sessions_limit });
        } catch (e) {
            localStorage.removeItem("vivoz_token");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Server-side plan/token changes (team invites, subscription renewals via
    // webhook, admin adjustments) don't push to the client — resync on an
    // interval and whenever the tab regains focus so they show up without
    // forcing a manual reload.
    useEffect(() => {
        if (!localStorage.getItem("vivoz_token")) return;
        const interval = setInterval(refresh, 30000);
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, [user, refresh]);

    const login = async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        localStorage.setItem("vivoz_token", r.data.token);
        await refresh();
        return r.data.user;
    };

    const register = async (email, password, name, language) => {
        const r = await api.post("/auth/register", { email, password, name, language });
        localStorage.setItem("vivoz_token", r.data.token);
        await refresh();
        trackMetaEvent("CompleteRegistration");
        return r.data.user;
    };

    const googleAuth = async (credential) => {
        const r = await api.post("/auth/google", { credential });
        localStorage.setItem("vivoz_token", r.data.token);
        await refresh();
        // Google login can also authenticate an existing account — only
        // count it as a conversion when it actually created a new one.
        if (r.data.new_user) trackMetaEvent("CompleteRegistration");
        return r.data.user;
    };

    const logout = () => {
        localStorage.removeItem("vivoz_token");
        setUser(null); setTokens(0); setPlan("free"); setAllowedScenarios(null);
        setDailyUsage({ used: 0, limit: null });
    };

    const updateSettings = async (payload) => {
        const r = await api.patch("/auth/settings", payload);
        await refresh();
        return r.data.user;
    };

    return (
        <AuthCtx.Provider value={{ user, tokens, plan, allowedScenarios, dailyUsage, loading, login, register, googleAuth, logout, refresh, updateSettings }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
